import { useEffect, useState } from "react";
import { ArrowDown, Github, Linkedin, Mail } from "lucide-react";
import { useTheme } from "./ThemeContext";

const titles = ["QA Automation Engineer", "Software Developer", "Selenium + Java Builder", "Full-Stack Developer"];

export default function Hero() {
  const { theme } = useTheme();
  const [titleIdx, setTitleIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [typing, setTyping] = useState(true);

  useEffect(() => {
    const target = titles[titleIdx];
    if (typing && displayed.length < target.length) {
      const t = setTimeout(() => setDisplayed(target.slice(0, displayed.length + 1)), 60);
      return () => clearTimeout(t);
    }
    if (typing) {
      const t = setTimeout(() => setTyping(false), 1800);
      return () => clearTimeout(t);
    }
    if (displayed.length > 0) {
      const t = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 35);
      return () => clearTimeout(t);
    }
    setTitleIdx((i) => (i + 1) % titles.length);
    setTyping(true);
  }, [displayed, typing, titleIdx]);

  return (
    <section className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden px-4 py-24 sm:px-6 sm:pt-20" style={{ background: "var(--background)" }}>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: theme === "dark"
            ? "linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)"
            : "linear-gradient(rgba(0,87,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,87,255,0.05) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      <div
        className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ width: "min(600px, 120vw)", height: "min(600px, 120vw)", background: theme === "dark" ? "radial-gradient(circle, rgba(0,212,255,0.07) 0%, transparent 70%)" : "radial-gradient(circle, rgba(0,87,255,0.07) 0%, transparent 70%)" }}
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl">
        <div className="mb-6 inline-flex max-w-full items-center gap-2 rounded border px-3 py-1.5 sm:mb-8" style={{ fontFamily: "var(--font-mono)", fontSize: "clamp(0.625rem, 2.8vw, 0.75rem)", color: "var(--accent)", borderColor: "rgba(0,255,157,0.2)", background: "rgba(0,255,157,0.06)", letterSpacing: "0.06em" }}>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: "var(--accent)" }} />
          AVAILABLE FOR OPPORTUNITIES
        </div>

        <h1 className="mb-4 break-words" style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2.15rem, 13vw, 5.5rem)", fontWeight: 600, lineHeight: 1.08, color: "var(--foreground)" }}>
          Ibrahim Mustapha
        </h1>

        <div className="mb-8 flex min-h-12 flex-wrap items-center break-words" style={{ fontFamily: "var(--font-mono)", fontSize: "clamp(0.875rem, 4.3vw, 1.375rem)", color: "var(--primary)" }}>
          <span style={{ color: "var(--muted-foreground)", marginRight: "0.5rem" }}>$</span>
          {displayed}
          <span className="ml-0.5 animate-pulse" style={{ borderRight: "2px solid var(--primary)", height: "1.2em", display: "inline-block" }} />
        </div>

        <p className="mb-10 max-w-xl sm:mb-12" style={{ fontFamily: "var(--font-sans)", fontSize: "clamp(0.9375rem, 3.8vw, 1.0625rem)", lineHeight: 1.7, color: "var(--muted-foreground)" }}>
          I build dependable software and test automation that protects the user experience. Based in Ghana, I work with Selenium, Java, Jenkins, React, and TypeScript to help teams ship with confidence.
        </p>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          <button onClick={() => document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" })} className="rounded px-6 py-3 transition-all duration-200 hover:opacity-90" style={{ background: "var(--primary)", color: "var(--primary-foreground)", fontFamily: "var(--font-sans)", fontSize: "0.875rem", fontWeight: 500 }}>
            View Projects
          </button>
          <button onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} className="rounded border px-6 py-3 transition-all duration-200" style={{ borderColor: "var(--border)", color: "var(--foreground)", fontFamily: "var(--font-sans)", fontSize: "0.875rem", fontWeight: 500, background: "transparent" }}>
            Get in Touch
          </button>
          <div className="flex items-center gap-3 sm:ml-2">
            {[
              { icon: Github, href: "https://github.com/ibrahimmustapha", label: "GitHub" },
              { icon: Linkedin, href: "https://www.linkedin.com/in/ibrahim-mustapha-a12a891a8/", label: "LinkedIn" },
              { icon: Mail, href: "mailto:musib581@gmail.com", label: "Email" },
            ].map(({ icon: Icon, href, label }) => (
              <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noreferrer" : undefined} aria-label={label} className="flex h-9 w-9 items-center justify-center rounded border transition-all duration-200" style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}>
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <button onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })} className="absolute bottom-8 left-1/2 flex -translate-x-1/2 animate-bounce flex-col items-center gap-2" style={{ color: "var(--muted-foreground)" }} aria-label="Scroll down">
        <ArrowDown size={16} />
      </button>
    </section>
  );
}
